
import { Card, CardContent, CardFooter } from "@/components/ui/card";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { QuoteIcon } from "lucide-react";
import { useTranslation } from 'react-i18next';
import { useScrollAnimation } from "@/hooks/useScrollAnimation";

const testimonials = [
  {
    id: 1,
    content: "Working with Amara has transformed my life. Her holistic therapy sessions helped me overcome chronic anxiety that I've struggled with for years. I feel more balanced and at peace than ever before.",
    author: "Jessica T.",
    role: "Marketing Executive",
    avatar: "https://images.unsplash.com/photo-1494790108377-be9c29b29330?ixlib=rb-1.2.1&auto=format&fit=crop&w=500&q=60"
  },
  {
    id: 2,
    content: "David's meditation guidance helped me develop a daily practice that has completely changed how I handle stress. I'm more productive at work and more present with my family.",
    author: "Michael K.",
    role: "Software Engineer",
    avatar: "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?ixlib=rb-1.2.1&auto=format&fit=crop&w=500&q=60"
  },
  {
    id: 3,
    content: "I was skeptical at first, but Sophia's professional counseling gave me clarity during one of the most difficult transitions in my life. Her intuitive guidance was exactly what I needed.",
    author: "Alicia R.",
    role: "Teacher",
    avatar: "https://images.unsplash.com/photo-1438761681033-6461ffad8d80?ixlib=rb-1.2.1&auto=format&fit=crop&w=500&q=60"
  },
  {
    id: 4,
    content: "The mind-body therapy sessions I had with Resonow practitioners helped me reconnect with myself in ways I never thought possible. I feel like I'm finally living authentically.",
    author: "Thomas P.",
    role: "Architect",
    avatar: "https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?ixlib=rb-1.2.1&auto=format&fit=crop&w=500&q=60"
  },
];

const Testimonials = () => {
  const { t } = useTranslation('homepage');
  const { ref, isVisible } = useScrollAnimation({ threshold: 0.1 });

  return (
    <section 
      ref={ref}
      className={`py-16 bg-amber-50 transition-all duration-700 ${
        isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
      }`}
      data-testid="testimonials"
    >
      <div className="container mx-auto px-4">
        <div className="text-center mb-12">
          <h2 className="text-3xl md:text-4xl font-serif font-medium text-earth-900 mb-4">
            {t('healingStories')}
          </h2>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 lg:gap-8">
          {testimonials.map((testimonial, index) => (
            <Card 
              key={testimonial.id} 
              className={`bg-white border-amber-100 card-hover transition-all duration-500 ${
                isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
              }`}
              style={{ transitionDelay: `${index * 150}ms` }}
            >
              <CardContent className="pt-6 relative">
                <QuoteIcon className="h-8 w-8 text-amber-200 absolute top-6 right-6" />
                <p className="text-earth-700 leading-relaxed relative z-10">{testimonial.content}</p>
              </CardContent>
              <CardFooter className="flex items-center pt-2 pb-6">
                <Avatar className="h-10 w-10 mr-3 flex-shrink-0">
                  <AvatarImage 
                    src={testimonial.avatar} 
                    alt={testimonial.author}
                    className="object-cover"
                  />
                  <AvatarFallback>{testimonial.author.split(' ')[0][0]}{testimonial.author.split(' ')[1][0]}</AvatarFallback>
                </Avatar>
                <div>
                  <p className="font-medium text-earth-900">{testimonial.author}</p>
                  <p className="text-sm text-earth-500">{testimonial.role}</p>
                </div>
              </CardFooter>
            </Card>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Testimonials;
